import type { Severity } from '../../types/diagnosis';

interface SeverityBadgeProps {
  severity: Severity;
  size?: 'sm' | 'md';
  className?: string;
}

const severityConfig: Record<
  Severity,
  {
    icon: string;
    label: string;
    iconColor: string;
  }
> = {
  minor: {
    icon: 'check_circle',
    label: 'Minor',
    iconColor: 'text-status-green',
  },
  moderate: {
    icon: 'warning',
    label: 'Moderate',
    iconColor: 'text-editorial-gold',
  },
  severe: {
    icon: 'error',
    label: 'Urgent',
    iconColor: 'text-danger',
  },
};

/**
 * Editorial severity badge — pill with icon + uppercase label
 */
export function SeverityBadge({ severity, size = 'md', className = '' }: SeverityBadgeProps) {
  const config = severityConfig[severity];

  const sizeClasses = {
    sm: 'gap-1.5 px-2.5 py-1',
    md: 'gap-2 px-3 py-1.5',
  };

  const iconSize = {
    sm: 14,
    md: 16,
  };

  const textClasses = {
    sm: 'text-[10px]',
    md: 'text-xs',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full bg-white/95 backdrop-blur border border-editorial-gold shadow-sm ${sizeClasses[size]} ${className}`}
      role="status"
      aria-label={`Severity: ${config.label}`}
    >
      <span
        className={`material-symbols-outlined ${config.iconColor}`}
        style={{ fontSize: iconSize[size] }}
        aria-hidden="true"
      >
        {config.icon}
      </span>
      <span className={`font-bold tracking-widest text-editorial-black uppercase ${textClasses[size]}`}>
        {config.label}
      </span>
    </span>
  );
}
